/**
 * Asset Path Configuration
 * Centralized asset imports for SpeedyShip
 */

// Image paths (for Vite, these will be resolved at build time)
export const images = {
  // Logo
  logo: '/images/logo.png',

  // Backgrounds
  backgrounds: {
    hero: '/images/hero-bg.webp',
    cta: '/images/cta-bg.webp',
  },

  // Content Images
  content: {
    deliveryServices: '/images/delivery-services.webp',
    happyDeliverer: '/images/happy-deliverer.webp',
    dailyPayment: '/images/daily-payment.jpg',
    heroIllustration: '/images/hero-illustration.webp',
    personDeliverer: '/images/person-deliverer.webp',
    redShirtDeliverer: '/images/person-red-shirt.webp',
    businessPerson: '/images/business-person-new.webp',
  },

  // Service Images (from original SpeedyShip website)
  services: {
    standardDelivery: '/images/services/service-1.webp',
    rushDelivery: '/images/services/service-2.webp',
    directDelivery: '/images/services/service-3.webp',
    medicalTransport: '/images/services/service-4.webp',
    temperatureControlled: '/images/services/service-5.webp',
    freightLogistics: '/images/services/service-6.webp',
    warehousing: '/images/services/service-7.webp',
  },

  // Business Page Images
  business: {
    hero: '/images/business/hero-business.jpg',
    background: '/images/business/bg-3.png',
    circleStats: '/images/business/circle.svg',
  },

  // How It Works Images
  howItWorks: {
    dailyPayment: '/images/howitworks/daily-payment.jpg',
  },

  // Decorative SVGs
  decorative: {
    line1: '/images/line-1.svg',
    line8: '/images/line-8.svg',
    ellipse1: '/images/ellipse-1.svg',
    circleBlue: '/images/Ellipse-1-decorative-light.svg',
  },
} as const;

// Alt text for accessibility
export const imageAltText = {
  logo: 'SpeedyShip - Fast & Reliable Courier Service',

  backgrounds: {
    hero: 'SpeedyShip delivery network across British Columbia',
    cta: 'Join SpeedyShip business delivery network',
  },

  content: {
    deliveryServices: 'Online delivery services - courier by truck, scooter, and bicycle with real-time tracking',
    happyDeliverer: 'Professional SpeedyShip deliverer carrying packages for home delivery',
    dailyPayment: 'Daily payment options for SpeedyShip business accounts',
    heroIllustration: 'SpeedyShip courier services - delivery by truck, scooter, bicycle with mobile tracking',
    personDeliverer: 'Professional SpeedyShip courier delivering packages',
    redShirtDeliverer: 'Professional SpeedyShip delivery courier in red uniform',
    businessPerson: 'Professional SpeedyShip business account representative',
  },

  decorative: {
    line1: 'Decorative line element',
    line8: 'Decorative line element',
    ellipse1: 'Decorative shape element',
    circleBlue: 'Blue circular decorative background',
  },

  // Service Images Alt Text
  services: {
    standardDelivery: 'Happy delivery person carrying packages for home delivery',
    rushDelivery: 'Front view of delivery person with packages for rush delivery',
    directDelivery: 'Delivery person handing package to smiling customer',
    medicalTransport: 'Professional courier on phone coordinating medical transport delivery',
    temperatureControlled: 'Courier in protective gear handling temperature-controlled packages',
    freightLogistics: 'Professional delivery driver with packages for freight logistics',
    warehousing: 'Warehousing and fulfillment operations in professional facility',
  },

  // Business Page Alt Text
  business: {
    hero: 'Professional business delivery service team',
    background: 'SpeedyShip business services background pattern',
    circleStats: 'Business statistics and metrics visualization',
  },

  // How It Works Alt Text
  howItWorks: {
    dailyPayment: 'SpeedyShip delivery process: Book, Pick Up, Deliver, Receive Confirmation',
  },
} as const;

// Helper function to get image with alt text
export const getImage = (category: keyof typeof images, name?: string) => {
  if (name && typeof images[category] === 'object') {
    const imagePath = (images[category] as any)[name];
    const altText = (imageAltText[category] as any)[name];
    return { src: imagePath, alt: altText };
  }

  const imagePath = images[category];
  const altText = imageAltText[category];
  return { src: imagePath, alt: altText };
};

// Image component props helper
export const createImageProps = (
  src: string,
  alt: string,
  options?: {
    loading?: 'lazy' | 'eager';
    className?: string;
    width?: number;
    height?: number;
  }
) => ({
  src,
  alt,
  loading: options?.loading || 'lazy',
  className: options?.className || '',
  ...(options?.width && { width: options.width }),
  ...(options?.height && { height: options.height }),
});

// Usage examples:
// const logoProps = getImage('logo');
// <img {...logoProps} />
//
// const heroProps = getImage('backgrounds', 'hero');
// <div style={{ backgroundImage: `url(${heroProps.src})` }} />

export default images;
